/* eslint-disable max-len */
import React, {useEffect, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useHistory, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import Lottie from 'lottie-web';
import styled, {keyframes} from 'styled-components';
import {Helmet} from 'react-helmet';

import {
    Flex,
    isMobile,
    mobileMediaQuery,
    tabletMediaQuery,
    notWebMediaQuery,
    PageBodyWrapper,
    useBreakpoint,
    notMobileMediaQuery,
    webMediaQuery,
} from './../styles';
import {
    contentUrlSolver,
    formatContentDuration,
    capitalize,
    useGetUserToken,
    slugify,
} from './../helpers';

import YorhaBitmovin from '@tides/yorha-react';
import '@tides/yorha-react/dist/main.css';

import ContentBox from './../components/ContentBox';
import Switch from '../components/Switch';
import Button from './../components/Button';
import Classification from './../components/Classification';

import * as EpisodeActions from '../store/actions/episode';
import * as VideoActions from '../store/actions/video';
import * as DrmActions from '../store/actions/drm';
import * as FavouriteActions from '../store/actions/favourite';
import * as ToastActions from '../store/actions/toast';
import {episodeStarted, episodeFinished} from '../store/actions/recommendation';

import {ReactComponent as ArrowIcon} from './../assets/arrow.svg';
import HeartIcon from './../assets/favourites_icon.png';

import Loader from '../components/Loader';
import {FavouriteButton as FavButton, FavouriteIcon} from './ShowDetails';
import PlayerError from '../components/PlayerError';
import ContentRow from '../components/ContentRow';

import CNLoader from '../assets/animations/CNLoader.json';
import api, {graphqlQueryBuild} from '../services/api';

const Container = styled(PageBodyWrapper)`
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    padding: 1rem 0;
    min-height: calc(100vh - 392px);

    .error-display {
        display: none;
    }

    ${notWebMediaQuery} {
        max-width: 100%;
        padding-top: 78px;
    }

    ${mobileMediaQuery} {
        padding: 78px 0 0 0;
    }
`;

const Col = styled.div`
    display: flex;
    flex-direction: column;
    margin: ${props => props.margin && props.margin};
    flex: ${props => props.flex && props.flex};
    justify-content: ${props => props.justifyContent && props.justifyContent};
    align-items: ${props => props.alignItems && props.alignItems};
`;

const Grid = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: repeat(16, 1fr);
    grid-gap: 32px;
    padding-bottom: 2rem;
    padding: 0px 0px;
    box-sizing: border-box;
    background-color: #211921;

    :not(:last-child) {
        margin-bottom: 30px;
    }

    @media screen and (max-width: 1024px) {
        display: flex;
        flex-direction: column;
    }

    ${mobileMediaQuery} {
        padding: 0px 0px;
    }
    ${webMediaQuery} {
        padding: 18px 0px 0px 0px;
    }
`;

const Separator = styled.hr`
    height: 2px;
    width: 100%;
    grid-column-start: 1;
    grid-column-end: 17;
    border: none;
    padding: none;
    margin: 0;
    background: linear-gradient(
        90deg,
        #ffd93f 0%,
        #ffd93f 4.79%,
        #00aeef 28.08%,
        #ffd93f 38.29%,
        #ffd93f 64.14%,
        #ec008c 86.86%,
        #ffd93f 96.35%
    );
`;

const GridColumn = styled.div`
    display: flex;
    flex-direction: column;
    grid-column-start: ${props => props.start};
    grid-column-end: ${props => props.end};

    :nth-of-type(2)::after {
        content: '';
        height: 42px;

        ${notWebMediaQuery} {
            content: initial;
            height: 0px;
        }
    }

    @media screen and (max-width: 1312px) {
        :nth-of-type(1) {
            grid-column-end: ${props => props.end - 1};
        }
        :nth-of-type(2) {
            grid-column-start: ${props => props.start - 1};
        }
    }
`;

const UpNextContainer = styled(GridColumn)`
    position: relative;
    h3 {
        font-family: 'TTNormsProExtraBlack';
        font-size: 18px;
        line-height: 18px;
    }
    & > div:nth-of-type(2) {
        position: relative;
        display: grid;
        grid-gap: 1rem;
        grid-auto-flow: row;
        height: auto;
        max-height: calc(100vh - 250px);

        @media screen and (max-width: 1024px) {
            max-height: none;
        }

        overflow-y: auto;
        &::-webkit-scrollbar {
            width: 8px;
        }
        &::-webkit-scrollbar-track {
            background: #000;
        }
        &::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.6);
            border-radius: 4px;
        }
        &::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.75);
        }
    }
    & > div:hover {
        &::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.75);
        }
    }
    ${notWebMediaQuery} {
        padding: 0px 40px;
    }

    ${mobileMediaQuery} {
        padding: 0px 25px;
    }
`;

const Row = styled(Col)`
    flex-direction: row !important;
    width: ${props => props.width && props.width};
    margin: ${props => props.margin && props.margin};
    flex: ${props => props.flex && props.flex};
    position: relative;
`;

const BackgroundOverlay = styled.div`
    position: absolute;
    top: 0px;
    bottom: 0px;
    left: 0px;
    right: 0px;
    pointer-events: none;
`;

const VideoPlayerContainer = styled.div`
    width: 100%;
    height: auto;
    margin-bottom: 2rem;
    .bitmovinplayer-container {
        width: 100% !important;
    }

    & > div {
        position: relative;
    }

    ${webMediaQuery} {
        padding: 0px 0px 0px 18px;
    }
`;

const GradientOverlay = styled.div`
    position: absolute;
    top: 60px;
    bottom: 0;
    left: 0;
    right: 0;
    pointer-events: none;
    background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 90%,
        rgba(0, 0, 0, 1) 98%
    );
    z-index: 99;

    ${notWebMediaQuery} {
        display: none;
    }
`;

const Title = styled.h2`
    font-family: 'AvenirNextRoundedStd';

    color: rgb(157, 157, 157);
    font-size: 14px;
    line-height: 18px;
    font-weight: normal;
    text-transform: uppercase;
    padding: 0px;
    margin: 0px;
    margin-bottom: 5px;
    max-width: 100%;

    ${mobileMediaQuery} {
        margin-bottom: 4px;
    }
`;

const SubTitle = styled(Title)`
    font-family: 'TTNormsProExtraBlack';
    color: #fff;
    text-transform: uppercase;
    font-size: 20px;
    line-height: 31px;
    margin-bottom: 9px;
    font-weight: bold;

    ${mobileMediaQuery} {
        font-weight: bold;
        font-size: 18px;
    }
`;

const Description = styled.p`
    color: #fff;
    font-size: 18px;
    line-height: 24px;
    margin: 0;
    padding: 0;
    margin-bottom: 6px;

    ${tabletMediaQuery} {
        margin-bottom: 12px;
    }

    ${mobileMediaQuery} {
        margin-bottom: 7px;
        font-size: 16px;
        line-height: 22px;
    }
`;

const RoundedIcon = styled.button`
    margin: 0;
    width: 48px;
    height: 48px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    border: 0;
    background-color: white;
    ${props =>
        props.backgroundUrl &&
        `
        background-image: url(${props.backgroundUrl});
    `}
    background-size: cover;
    background-position: 50% 50%;
    background-repeat: no-repeat;
    overflow: hidden;
    &:not(:last-child) {
        margin-right: 1rem;
    }
    cursor: pointer;
`;

const ButtonTitle = styled.h6`
    font-family: TTNormsProExtraBlack;
    margin: 0;
    text-transform: uppercase;
    font-size: 16px;
    line-height: 24px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
`;

const Arrow = styled(ArrowIcon)`
    margin-left: 12px;
    path {
        fill: #ffffff;
    }
`;

const CarrouselContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    margin: 0;
`;

const FavouriteButton = styled(FavButton)`
    position: relative;
    bottom: 0px;
`;

const AboutShowButton = styled(Button)`
    ${mobileMediaQuery} {
        width: 100%;
        border-radius: 0px;
        background: rgb(58, 51, 58);
        font-size: 16px;
        line-height: 24px;
    }
    ${notMobileMediaQuery} {
        margin-left: 18px;
        background: black;
    }
    margin-top: 24px;
`;

const heartAnimation = keyframes`
    0% {
        transform: rotate(0deg);
    }

    50% {
        transform: rotate(40deg);
    }

    75% {
        transform:  rotate(-30deg);
    }



    100% {
        transform: rotate(0deg);
    }
`;

const AnimatedHeartIcon = styled.img`
    transform-origin: center bottom;
    &[data-animated='true'] {
        animation: ${heartAnimation} 0.5s ease;
    }
`;

const rateLevel = {
    5: {
        label: 'LIVRE',
        value: 'L',
        color: '#00a651',
    },
    27: {
        label: '10 ANOS',
        value: '10',
        color: '#0095DA',
    },
    28: {
        label: '12 ANOS',
        value: '12',
        color: '#FBC115',
    },
    51: {
        label: '14 ANOS',
        value: '14',
        color: '#F58220',
    },
    77: {
        label: '16 ANOS',
        value: '16',
        color: '#EC1D25',
    },
    78: {
        label: '18 ANOS',
        value: '18',
        color: '#000',
    },
};

export const Player = props => {
    const history = useHistory();
    const params = useParams();
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const config = useSelector(state => state.config);

    props = {
        ...props,
        ...useSelector(state => {
            return {
                ...state.episode,
                ...state.show,
                ...state.drm,
                ...state.video,
            };
        }),
    };

    const user = useSelector(state => state.user);
    const idp = user && user.idp;
    const tve = user && user.user_type === 'tve';

    const favourites = useSelector(state => state.favourite);
    const userToken = useGetUserToken();

    const [animateIcon, setAnimateIcon] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [favourite, setFavourite] = useState(false);
    const [pageState, setPageState] = useState('loading');
    const [currentShow, setCurrentShow] = useState(undefined);
    const [currentContent, setCurrentContent] = useState(undefined);
    const [upNext, setUpNext] = useState(undefined);
    const [contentSrc, setContentSrc] = useState(undefined);
    const [drmToken, setDrmToken] = useState(undefined);
    const [mightLike, setMightLike] = useState(undefined);
    const [readyToNext, setReadyToNext] = useState(false);
    const [alreadyChanged, setAlreadyChanged] = useState(false);
    const [playerReady, setPlayerReady] = useState(false);
    const [adStarted, setAdStarted] = useState(false);
    const [startedHeartBeat, setStartedHearBeat] = useState(false);
    const [playerError, setPlayerError] = useState(null);
    const [bingecounter, setBingecounter] = useState(0);
    const [autoPlay, setAutoPlay] = useState(
        localStorage.getItem('playback_autoplay')
            ? localStorage.getItem('playback_autoplay') === 'true'
                ? true
                : false
            : true,
    );
    const [browserOffline, setBrowserOffline] = useState(false);
    const [stalling, setStalling] = useState(false);

    useEffect(() => {
        window.scrollTo({top: 0});

        window.addEventListener('focus', () => {
            !isFocused && setIsFocused(true);
        });

        window.addEventListener('click', () => {
            !isFocused && setIsFocused(true);
        });

        window.addEventListener('offline', e => {
            setBrowserOffline(true);
        });

        window.addEventListener('online', e => {
            setBrowserOffline(false);
        });

        return () => {
            window.turner_metadata.trackAction.push({
                type: 'video',
                subtype: 'Content_Completed',
                data: {
                    authstate:
                        user && user.jwtToken
                            ? 'authenticated'
                            : 'unauthenticated',
                    mvpd: idp || 'idp not loaded',
                },
            });

            setStartedHearBeat(false);
            setPlayerReady(false);
            setAdStarted(false);
            setReadyToNext(false);
            setAlreadyChanged(false);
        };

        // eslint-disable-next-line
    }, [window.location.pathname]);
    let breakpoint = useBreakpoint();

    useEffect(() => {
        return () => {
            if (userToken !== undefined) {
                let url = `${(config.catalog_settings &&
                    config.catalog_settings.endpoint) ||
                    'https://avs-proxy.dev.aaa.wmgp.io/v1'}/R/${
                    user.catalogLanguage
                }/${
                    config.catalog_settings && config.catalog_settings.platforms
                        ? config.catalog_settings.platforms[0]
                        : 'PCTV_DASH'
                }/${user.catalogTenant ||
                    'CNPLUS_LATAM_LATAM'}/CONTENT/STOP?contentId=${
                    params.episode_id
                }&asJson=Y&type=VOD&bookmark=0&deltaThreshold=0`;

                let headers = new Headers();
                headers.append('Authorization', `Bearer ${userToken}`);

                fetch(url, {headers}).then(res => res.json());
            }
        };

        // eslint-disable-next-line
    }, [userToken, window.location.pathname]);

    useEffect(() => {
        handleConnectionStatus();

        // eslint-disable-next-line
    }, [browserOffline, stalling]);

    useEffect(() => {
        setPageState('loading');
        setCurrentContent(undefined);
        setUpNext(undefined);
        setMightLike(undefined);
        setDrmToken(undefined);
        setContentSrc(undefined);

        if (
            props.episode
                ? props.episode[params.episode_id] === undefined
                : true
        ) {
            dispatch(EpisodeActions.loadEpisode(params.episode_id));
        }

        if (props.videos ? !props.videos[params.episode_id] : true) {
            preparePlayback();
        }

        if (user.jwtToken) {
            dispatch(DrmActions.loadDRMToken(params.episode_id, user.jwtToken));
        }

        //eslint-disable-next-line
    }, [params.episode_id]);

    useEffect(() => {
        if (
            props.episode &&
            props.episode[params.episode_id] &&
            params.episode_id === props.episode[params.episode_id].id.toString()
        ) {
            setCurrentContent(props.episode[params.episode_id]);
        }

        //eslint-disable-next-line
    }, [props.episode, params.episode_id]);

    useEffect(() => {
        if (currentContent !== undefined) {
            setFavourite(
                favourites.episodes
                    ? favourites.episodes.find(
                          item =>
                              item.id.toString() ===
                              currentContent.id.toString(),
                      )
                    : false,
            );

            currentContent.objectSubtype !== 'MOVIE' && loadRecomendation();
            loadUpNext();
        }
        //eslint-disable-next-line
    }, [currentContent]);

    useEffect(() => {
        if (currentContent !== undefined) {
            if (currentContent.show) {
                setCurrentShow(currentContent.show);
            } else {
                setCurrentShow({});
            }
        }

        // eslint-disable-next-line
    }, [currentContent]);

    useEffect(() => {
        if (props.videos && props.videos[params.episode_id]) {
            setContentSrc(props.videos[params.episode_id].src);
        }
    }, [props.videos, params.episode_id]);

    useEffect(() => {
        if (props.tokens && props.tokens[params.episode_id]) {
            setDrmToken(props.tokens[params.episode_id]);
        }
    }, [props.tokens, params.episode_id]);

    useEffect(() => {
        if (
            currentContent !== undefined &&
            (upNext !== undefined ||
                currentContent.objectSubtype === 'MOVIE') &&
            pageState !== 'ready' &&
            currentShow !== undefined
        ) {
            setPageState('ready');

            window.turner_metadata.trackAction.push({
                type: 'pageview',
                data: {
                    section: 'Video Playback',
                    subsection: 'nvs',
                    franchise: currentShow.title,
                    contenttype: 'Video Playback',
                    contenttitle: 'Video Playback',
                    presentationtemplate: 'desktop',
                    platform: 'web',
                    domain: 'cnplus.com',
                    region: 'latam',
                    country:
                        user && user.countryIsoCode && user.countryName
                            ? `${user.countryIsoCode.toLowerCase()}|${
                                  user.countryName
                              }`
                            : 'not loaded',
                    brand: 'cartoon network',
                    pageviewevent: 1,
                    authstate:
                        user && user.jwtToken
                            ? 'authenticated'
                            : 'unauthenticated',
                    mvpd: idp || 'idp not loaded',
                },
            });
        }

        // eslint-disable-next-line
    }, [currentContent, upNext, currentShow, idp, pageState]);

    useEffect(() => {
        if (pageState === 'ready' && currentContent && currentContent.show) {
            dispatch(episodeStarted(currentContent));
        }

        // eslint-disable-next-line
    }, [pageState]);

    useEffect(() => {
        if (playerReady) {
            const stallIndicator = document.getElementById(
                'yui-stall-indicator',
            );

            if (stallIndicator) {
                Lottie.loadAnimation({
                    container: document.getElementById('yui-stall-indicator'),
                    renderer: 'svg',
                    loop: true,
                    autoplay: true,
                    animationData: CNLoader,
                    isClickToPauseDisabled: true,
                    isStopped: false,
                    isPaused: false,
                });
            }
        }

        // eslint-disable-next-line
    }, [playerReady]);

    const handleConnectionStatus = () => {
        if (browserOffline && !navigator.onLine && stalling) {
            setPlayerError({
                title: t('player.offline.title'),
                errorMessage: t('player.offline.message'),
                hideHero: true,
                buttonText: t('player.offline.cta'),
                onClickButton: recheckConnection,
            });
        } else {
            setPlayerError(null);
        }
    };

    const recheckConnection = () => {
        setPlayerError(null);

        setTimeout(handleConnectionStatus, 5000);
    };

    const preparePlayback = async () => {
        let response = await dispatch(
            !user.jwtToken
                ? VideoActions.preparePlaybackFreeContent(params.episode_id)
                : VideoActions.preparePlayback(
                      params.episode_id,
                      user.jwtToken,
                  ),
        );

        if (!!response.data?.data?.playback) {
            setContentSrc(response.data.data.playback.url);
            setDrmToken(response.data.data.playback.drmToken);
        }

        if (
            response.resultCode !== 'OK' &&
            response.errorDescription === 'FRUITION_EXCEED'
        ) {
            dispatch({
                type: 'MODAL_SHOW',
                modal: {
                    visible: true,
                    title: t('video.concurrencyError'),
                    buttons: (
                        <>
                            <Button
                                margin="0 1rem 0 auto"
                                onClick={() => dispatch({type: 'MODAL_CLOSE'})}
                            >
                                Ok
                            </Button>
                        </>
                    ),
                },
            });

            setPlayerError({errorMessage: t('video.concurrencyError')});
        } else if (response.errorDescription === 'WM_100') {
            setPlayerError({errorMessage: t('onnow.geoblockError')});
        }
    };

    const handleToggleFavorite = e => {
        let episode = {
            ...currentContent,
        };

        setAnimateIcon(!favourite);

        !favourite
            ? dispatch(FavouriteActions.addEpisodeFavourite(episode))
            : dispatch(FavouriteActions.removeEpisodeFavourite(episode));

        dispatch(
            ToastActions.show(
                <>
                    <FavouriteIcon
                        src={HeartIcon}
                        active={!favourite ? 'true' : null}
                    />
                    {t(`video.${!favourite ? 'favorited' : 'unfavorited'}`)}
                </>,
            ),
        );

        setFavourite(!favourite);
    };

    const loadRecomendation = async () => {
        if (!currentContent.show?.id) {
            setMightLike([]);
            return;
        }

        let query = graphqlQueryBuild(
            'similarVods',
            'id title image duration episode season description show { title }',
            `id: ${currentContent.id}`,
        );

        let recommended_response = await api
            .get('', {
                params: {
                    query,
                },
            })
            .then(res => res)
            .catch(err => err.response);

        if (
            recommended_response.status === 200 &&
            recommended_response.data?.data?.similarVods
        ) {
            recommended_response.data.data.similarVods.map(vod => {
                vod.objectSubtype =
                    vod.season >= 1 && vod.episode >= 1 ? 'VIDEO' : 'MOVIE';

                return vod;
            });

            setMightLike(recommended_response.data.data.similarVods);
        } else {
            setMightLike([]);
        }
    };

    const loadUpNext = async () => {
        let query = graphqlQueryBuild(
            'upNext',
            'id title image duration episode season description',
            `id: ${currentContent.id}`,
        );

        let upnext_response = await api
            .get('', {
                params: {
                    query,
                },
            })
            .then(res => res)
            .catch(err => err.response);

        if (
            upnext_response.status === 200 &&
            upnext_response.data?.data?.upNext
        ) {
            upnext_response.data.data.upNext.map(vod => {
                vod.objectSubtype =
                    vod.season >= 1 && vod.episode >= 1 ? 'VIDEO' : 'MOVIE';

                return vod;
            });

            setUpNext(upnext_response.data?.data?.upNext);
        } else {
            setUpNext([]);
        }
    };

    const handleFindMoreAbout = () => {
        history.push(`/shows/${slugify(currentContent.title)}`);
    };

    const handleClickEp = content_id => {
        // window.location.href = `/play/${content_id}`;
        history.push(`/play/${content_id}`);
    };

    const handlePlayNextEpisode = () => {
        setReadyToNext(true);
    };

    useEffect(() => {
        if (
            autoPlay &&
            upNext &&
            upNext.length > 0 &&
            readyToNext &&
            !alreadyChanged
        ) {
            setTimeout(() => {
                history.push(`/play/${upNext[0].id}`);
            }, 2000);
            setAlreadyChanged(true);
        }
        // eslint-disable-next-line
    }, [readyToNext]);

    useEffect(() => {
        if (playerReady) {
            sendPlayerReadyAnalyticsCall();
        }

        // eslint-disable-next-line
    }, [playerReady]);

    useEffect(() => {
        if (playerReady && adStarted) {
            sendPlayerReadyAnalyticsCall();

            window.turner_metadata.trackAction.push({
                type: 'video',
                subtype: 'Ad_Started',
                data: {
                    ad_id: 'pre-roll-1' /* ad identifier */,
                    ad_duration: 0 /* ad duration in seconds */,
                    ad_type:
                        'preroll' /* ad type (e.g. preroll, midroll, postroll) */,
                    platform: 'web',
                    content_name:
                        currentContent.objectSubtype === 'MOVIE'
                            ? currentContent.title
                            : t('video.titleFormat')
                                  .replace('{season}', currentContent.season)
                                  .replace('{episode}', currentContent.episode),
                    content_id: currentContent.id.toString(),
                    content_type:
                        currentContent.objectSubtype === 'MOVIE'
                            ? 'movies'
                            : 'vod',
                    content_showName: currentContent.title,
                    content_seasonNumber: currentContent.season,
                    content_episodeNumber: currentContent.episode,

                    authstate:
                        user && user.jwtToken
                            ? 'authenticated'
                            : 'unauthenticated',
                    mvpd: idp || 'idp not loaded',
                    logintype: user && user.user_type,
                },
            });
        }

        // eslint-disable-next-line
    }, [playerReady, adStarted]);

    const handleChangeAutoPlay = value => {
        setAutoPlay(value);
        localStorage.setItem('playback_autoplay', value ? 'true' : 'false');
    };

    const renderMightLike = React.useMemo(() => {
        return mightLike === undefined ? (
            <Loader />
        ) : (
            <ContentRow
                titleAlign="left"
                title={t('video.recommended')}
                titleAs="h4"
                items={mightLike}
                onClickContent={handleClickEp}
            />
        );

        // eslint-disable-next-line
    }, [mightLike]);

    const memoizedPlayerComponent = React.useMemo(() => {
        if (
            (contentSrc === undefined ||
                drmToken === undefined ||
                pageState !== 'ready') &&
            !playerError
        ) {
            return <Loader />;
        } else {
            let adTagUrl = undefined;

            if (
                config.player_settings &&
                config.player_settings.ads &&
                config.player_settings.ads.enabled
            ) {
                adTagUrl = tve
                    ? config.player_settings.ads.tveAd
                    : config.player_settings.ads.telcoAd;

                if (config.btw && config.btw.enabled && !user.jwtToken) {
                    adTagUrl =
                        config.player_settings.ads.tveAd ||
                        config.player_settings.ads.telcoAd;
                }
            }

            if (adTagUrl && adTagUrl.indexOf('[countryname]')) {
                if (user.countryIsoCode) {
                    adTagUrl = adTagUrl.replace(
                        '[countryname]',
                        ['ar', 'br', 'co', 'cl', 'mx', 've'].includes(
                            user.countryIsoCode.toLowerCase(),
                        )
                            ? user.countryIsoCode.toLowerCase()
                            : 'la',
                    );
                } else {
                    adTagUrl = undefined;
                }
            }

            return (
                <>
                    {!playerReady && !playerError && (
                        <Loader style={{height: 'auto'}} />
                    )}

                    {playerError && <PlayerError {...playerError} />}

                    <div
                        style={
                            !playerReady || !!playerError
                                ? {display: 'none'}
                                : {}
                        }
                    >
                        <YorhaBitmovin
                            title={
                                currentContent.objectSubtype === 'MOVIE'
                                    ? `${currentContent.title}`
                                    : `[${currentShow && currentShow.title}] S${
                                          currentContent.season
                                      }:E${currentContent.episode}`
                            }
                            ispToken={drmToken}
                            license="59be50ed-f8b4-4ee3-8ec4-c24560a462ff"
                            preferredLanguage={
                                window.location.origin.includes('.br')
                                    ? 'POR'
                                    : 'SPA'
                            }
                            source={
                                config.user_device === 'IPHONE'
                                    ? {
                                          hls: contentSrc,
                                      }
                                    : {
                                          dash: contentSrc,
                                      }
                            }
                            oneTrust={true}
                            autoplay={config.user_device !== 'IPHONE'}
                            muted={
                                config.user_device === 'IPHONE'
                                    ? false
                                    : !isFocused
                            }
                            chromecast={false}
                            chromecastReceiverId={
                                config.player_settings.chromecast.receiverId
                            }
                            chromecastParams={{
                                metadata: {
                                    episode: currentContent.episode,
                                    season: currentContent.season,
                                    title: currentContent.title,
                                    seriesTitle: currentContent.description,
                                },
                                streamType:
                                    currentContent.objectSubtype === 'MOVIE'
                                        ? 'MOVIE'
                                        : 'VOD',
                                customData: {
                                    contentId: currentContent.id,
                                    brand: 'CNPLUS',
                                    device: 'PCTV',
                                    JWTToken: user.jwtToken,
                                    region: user.catalogTenant,
                                },
                            }}
                            customErrors={{
                                1400: 'AAAHHHH NOOOOOOOO',
                            }}
                            customStyle="https://cn.i.cdn.ti-platform.com/static/cnplus/cartoon-plus-reface.css"
                            widevineLAUrl={
                                config.player_settings.drm.widevine.LA_URL
                            }
                            fairplayLAUrl={
                                config.player_settings.drm.fairplay &&
                                config.player_settings.drm.fairplay.LA_URL
                            }
                            fairplayCertificateUrl={
                                config.player_settings.drm.fairplay &&
                                config.player_settings.drm.fairplay
                                    .certificateURL
                            }
                            youboraConfig={
                                currentContent && {
                                    accountCode:
                                        config.player_settings.analytics &&
                                        config.player_settings.analytics
                                            .youboraAccount
                                            ? config.player_settings.analytics
                                                  .youboraAccount
                                            : 'turnerlatam',
                                    contentIsLive: false,
                                    contentTitle:
                                        currentContent.objectSubtype === 'MOVIE'
                                            ? `${currentContent.title}`
                                            : `[${currentShow &&
                                                  currentShow.title}] S${
                                                  currentContent.season
                                              }:E${currentContent.episode}`,
                                    contentId: currentContent.id,
                                    username: idp,
                                    extraparams: {
                                        extraparam1: 'Cartoon Network',
                                        extraparam2: currentContent.season,
                                        extraparam3: currentContent.episode,
                                    },
                                }
                            }
                            localeSettings={
                                tve && {
                                    localeLanguage: window.location.origin.includes(
                                        '.br',
                                    )
                                        ? 'pt-BR'
                                        : 'es-ES',
                                }
                            }
                            adTagUrl={adTagUrl}
                            eventHandlers={{
                                onReady: () => {
                                    config.user_device === 'IPHONE' &&
                                        hideStallIndicator();

                                    setPlayerReady(true);
                                },
                                onPlay: () => {
                                    if (!playerReady) {
                                        setPlayerReady(true);
                                    }
                                },
                                onAdStarted: event => {
                                    setPlayerReady(true);

                                    setAdStarted(true);
                                },
                                onAdSkipped: () => {
                                    window.turner_metadata.trackAction.push({
                                        type: 'video',
                                        subtype: 'Ad_Skipped',
                                        data: {
                                            platform: 'web',
                                            content_name:
                                                currentContent.objectSubtype ===
                                                'MOVIE'
                                                    ? currentContent.title
                                                    : t('video.titleFormat')
                                                          .replace(
                                                              '{season}',
                                                              currentContent.season,
                                                          )
                                                          .replace(
                                                              '{episode}',
                                                              currentContent.episode,
                                                          ),
                                            content_id: currentContent.id.toString(),
                                            content_type:
                                                currentContent.objectSubtype ===
                                                'MOVIE'
                                                    ? 'movies'
                                                    : 'vod',
                                            content_showName:
                                                currentContent.title,
                                            content_seasonNumber:
                                                currentContent.season,
                                            content_episodeNumber:
                                                currentContent.episode,

                                            authstate:
                                                user && user.jwtToken
                                                    ? 'authenticated'
                                                    : 'unauthenticated',
                                            mvpd: idp || 'idp not loaded',
                                            logintype: user.user_type,
                                        },
                                    });
                                },

                                onAdFinished: () => {
                                    window.turner_metadata.trackAction.push({
                                        type: 'video',
                                        subtype: 'Ad_Finished',
                                        data: {
                                            platform: 'web',
                                            content_name:
                                                currentContent.objectSubtype ===
                                                'MOVIE'
                                                    ? currentContent.title
                                                    : t('video.titleFormat')
                                                          .replace(
                                                              '{season}',
                                                              currentContent.season,
                                                          )
                                                          .replace(
                                                              '{episode}',
                                                              currentContent.episode,
                                                          ),
                                            content_id: currentContent.id.toString(),
                                            content_type:
                                                currentContent.objectSubtype ===
                                                'MOVIE'
                                                    ? 'movies'
                                                    : 'vod',
                                            content_showName:
                                                currentContent.title,
                                            content_seasonNumber:
                                                currentContent.season,
                                            content_episodeNumber:
                                                currentContent.episode,

                                            authstate:
                                                user && user.jwtToken
                                                    ? 'authenticated'
                                                    : 'unauthenticated',
                                            mvpd: idp || 'idp not loaded',
                                            logintype: user.user_type,
                                        },
                                    });
                                },

                                onPlaying: () => {
                                    setReadyToNext(false);

                                    window.turner_metadata.trackAction.push({
                                        type: 'video',
                                        subtype: 'Media_Started',
                                        data: {
                                            authstate:
                                                user && user.jwtToken
                                                    ? 'authenticated'
                                                    : 'unauthenticated',
                                            mvpd: idp || 'idp not loaded',
                                        },
                                    });
                                },

                                onTimeChange: e => {
                                    window.turner_metadata.currentTime = parseInt(
                                        e.time,
                                    );
                                },

                                onStallStarted: () => {
                                    setStalling(true);

                                    window.turner_metadata.trackAction.push({
                                        type: 'video',
                                        subtype: 'Media_Buffering_Started',
                                        data: {
                                            authstate:
                                                user && user.jwtToken
                                                    ? 'authenticated'
                                                    : 'unauthenticated',
                                            mvpd: idp || 'idp not loaded',
                                        },
                                    });
                                },

                                onStallEnded: () => {
                                    setStalling(false);

                                    window.turner_metadata.trackAction.push({
                                        type: 'video',
                                        subtype: 'Media_Buffering_Finished',
                                        data: {
                                            authstate:
                                                user && user.jwtToken
                                                    ? 'authenticated'
                                                    : 'unauthenticated',
                                            mvpd: idp || 'idp not loaded',
                                        },
                                    });
                                },

                                onPause: () => {
                                    window.turner_metadata.trackAction.push({
                                        type: 'video',
                                        subtype: 'Media_Paused',
                                        data: {
                                            authstate:
                                                user && user.jwtToken
                                                    ? 'authenticated'
                                                    : 'unauthenticated',
                                            mvpd: idp || 'idp not loaded',
                                        },
                                    });
                                },

                                onPlaybackFinished: () => {
                                    window.turner_metadata.trackAction.push({
                                        type: 'video',
                                        subtype: 'Media_Finished',
                                        data: {
                                            authstate:
                                                user && user.jwtToken
                                                    ? 'authenticated'
                                                    : 'unauthenticated',
                                            mvpd: idp || 'idp not loaded',
                                        },
                                    });

                                    handlePlayNextEpisode();
                                },
                            }}
                        />
                    </div>
                </>
            );
        }

        // eslint-disable-next-line
    }, [
        contentSrc,
        drmToken,
        playerReady,
        playerError,
        currentContent,
        currentShow,
        pageState,
        startedHeartBeat,
        bingecounter,
        isFocused,
    ]);

    const sendPlayerReadyAnalyticsCall = () => {
        if (!startedHeartBeat) {
            currentContent.show && dispatch(episodeFinished(currentContent));

            window.turner_metadata.trackAction.push({
                type: 'video',
                subtype: 'Player_Ready',
                data: {
                    playerid: '',
                    content_duration: currentContent.duration,
                    content_dataCreated: '' /* YYYY-MM-DD (e.g. 2018-08-30) */,
                    content_dataAired: '' /* YYYY-MM-DD (e.g. 2018-08-30) */,
                    content_name:
                        currentContent.objectSubtype === 'MOVIE'
                            ? currentContent.title
                            : t('video.titleFormat')
                                  .replace('{season}', currentContent.season)
                                  .replace('{episode}', currentContent.episode),
                    content_id: currentContent.id.toString(),
                    content_type:
                        currentContent.objectSubtype === 'MOVIE'
                            ? 'movies'
                            : 'vod',
                    content_showName: currentContent.title,
                    content_seasonNumber: currentContent.season,
                    content_episodeNumber: currentContent.episode,
                    content_genre: currentContent.genres,
                    content_rating:
                        '' /* rating (e.g. tvy, tvg, tvpg, tvm, etc.) */,
                    content_originator:
                        '' /* originator (e.g. warnermedia, sony, disney, etc.) */,
                    content_network:
                        '' /* network (e.g. fox, brabo, espn, etc.) */,
                    content_mvpd:
                        '' /* mvpd (e.g. comcast, directv, dish, etc.) */,
                    content_authorized: '' /* true or false */,
                    day_part:
                        '' /* time of day (e.g. morning, daytime, primetime, etc.) */,
                    content_feed:
                        '' /* feed (e.g. east-hd, west-hd, east-sd, etc.) */,
                    authstate:
                        user && user.jwtToken
                            ? 'authenticated'
                            : 'unauthenticated',
                    mvpd: idp || 'idp not loaded',
                    logintype: user.user_type,
                    language: currentContent.metadataLanguage,
                    franchise: currentContent.title,
                    usercountry:
                        user && user.countryName
                            ? user.countryName
                            : 'not loaded',
                    avsid: currentContent.id,
                    houseid: currentContent.externalId,
                    producer: currentContent.contentProvider,
                    bingecounter: bingecounter + 1,
                    brand: 'cartoon network',
                },
            });

            setBingecounter(bingecounter + 1);
            setStartedHearBeat(true);
        }
    };

    const hideStallIndicator = () => {
        const stallIndicator = document.querySelector('#yui-stall-indicator');

        const pauseOverlay = document.querySelector('#yui-overlay-play');

        if (stallIndicator) {
            stallIndicator.classList.add('playing');
            stallIndicator.classList.remove('buffering');
        }

        if (pauseOverlay) {
            pauseOverlay
                .querySelector('div:last-child')
                .classList.remove('hidden');
            pauseOverlay
                .querySelector('div:last-child')
                .classList.remove('player-playing');
            pauseOverlay
                .querySelector('div:last-child')
                .classList.add('player-paused');
        }
    };

    return (
        <>
            <Helmet>
                <title>Cartoon Network App</title>
            </Helmet>
            <Container>
                {pageState !== 'ready' ? (
                    <Loader />
                ) : (
                    <>
                        {currentContent &&
                            (currentContent.objectSubtype !== 'MOVIE'
                                ? currentShow
                                : true) && (
                                <Helmet>
                                    <title>
                                        {currentContent.objectSubtype !==
                                        'MOVIE'
                                            ? `Assista ${capitalize(
                                                  currentShow.title,
                                                  true,
                                              )} ${t('video.titleFormat')
                                                  .replace(
                                                      '{season}',
                                                      currentContent.season,
                                                  )
                                                  .replace(
                                                      '{episode}',
                                                      currentContent.episode,
                                                  )} | Cartoon Network App`
                                            : `${capitalize(
                                                  currentContent.title,
                                                  true,
                                              )} | Cartoon Network App`}
                                    </title>
                                    <meta
                                        name="description"
                                        content={
                                            currentContent.objectSubtype !==
                                            'MOVIE'
                                                ? `${capitalize(
                                                      currentShow.title,
                                                      true,
                                                  )} ${capitalize(
                                                      t('shows.season'),
                                                  )} ${
                                                      currentContent.season
                                                  }, ${t(
                                                      'video.episode',
                                                  ).toLowerCase()} ${
                                                      currentContent.episode
                                                  }. ${
                                                      currentContent.description
                                                  }`
                                                : `${currentContent.description}`
                                        }
                                    ></meta>
                                </Helmet>
                            )}

                        <Grid>
                            <GridColumn start="1" end="12">
                                <VideoPlayerContainer>
                                    {memoizedPlayerComponent}
                                </VideoPlayerContainer>

                                <Flex
                                    width="100%"
                                    alignItems="flex-start"
                                    justifyContent="flex-start"
                                    direction="column"
                                    padding={
                                        isMobile ? '0px 18.5px' : '0px 18.5px'
                                    }
                                    boxSizing="border-box"
                                >
                                    <Row width="100%" alignItems="center">
                                        <Title
                                            as={
                                                currentContent.objectSubtype ===
                                                    'MOVIE' && 'h4'
                                            }
                                        >
                                            {currentContent.objectSubtype ===
                                            'MOVIE'
                                                ? t('movies.title')
                                                : currentContent.show.title}
                                        </Title>
                                        {currentContent.objectSubtype !==
                                            'MOVIE' &&
                                            !!user.jwtToken && (
                                                <FavouriteButton
                                                    active={favourite}
                                                    onClick={
                                                        handleToggleFavorite
                                                    }
                                                >
                                                    <AnimatedHeartIcon
                                                        src={HeartIcon}
                                                        data-animated={
                                                            animateIcon
                                                                ? 'true'
                                                                : 'false'
                                                        }
                                                        width="100%"
                                                    />
                                                </FavouriteButton>
                                            )}
                                    </Row>

                                    <SubTitle as="h1">
                                        {currentContent.objectSubtype ===
                                        'MOVIE'
                                            ? currentContent.title
                                            : `${t('video.titleFormat')
                                                  .replace(
                                                      '{season}',
                                                      currentContent.season,
                                                  )
                                                  .replace(
                                                      '{episode}',
                                                      currentContent.episode,
                                                  )} ${currentContent.title}`}
                                    </SubTitle>

                                    <Description>
                                        {currentContent.description}
                                    </Description>

                                    {currentContent.contentRating &&
                                        rateLevel[
                                            currentContent.contentRating
                                        ] && (
                                            <Classification
                                                color={
                                                    rateLevel[
                                                        currentContent
                                                            .contentRating
                                                    ].color
                                                }
                                                label=""
                                                value={
                                                    rateLevel[
                                                        currentContent
                                                            .contentRating
                                                    ].value
                                                }
                                            />
                                        )}
                                </Flex>

                                {currentShow && currentShow.id && (
                                    <Row>
                                        <AboutShowButton
                                            style={{padding: '14px 20px'}}
                                            onClick={handleFindMoreAbout}
                                            secondary
                                        >
                                            <RoundedIcon
                                                as="div"
                                                backgroundUrl={contentUrlSolver(
                                                    config,
                                                    currentShow &&
                                                        currentShow.image,
                                                    'SHOW-SELECTOR',
                                                    'png',
                                                )}
                                            ></RoundedIcon>
                                            <ButtonTitle>
                                                {t('video.findOut')}
                                                <Arrow
                                                    aria-hidden="true"
                                                    title=""
                                                    width="14px"
                                                    height="14px"
                                                />
                                            </ButtonTitle>
                                        </AboutShowButton>
                                    </Row>
                                )}
                                {['xs', 'mobile', 'sm', 'md'].includes(
                                    breakpoint,
                                ) && <Separator />}
                            </GridColumn>

                            <UpNextContainer start="12" end="17">
                                <Row
                                    margin="0 25px 24px 0px"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <h3>{t('video.upNext')}</h3>
                                    <Switch
                                        label={t('video.autoplay')}
                                        value={autoPlay}
                                        name="autoplay"
                                        onChange={handleChangeAutoPlay}
                                    />
                                </Row>
                                <div>
                                    {upNext === undefined ? (
                                        <Loader />
                                    ) : upNext.length === 0 ? (
                                        <h4>{t('video.noContent')}</h4>
                                    ) : (
                                        upNext.map((item, index) => {
                                            let contentSubtitle = t(
                                                'video.titleFormat',
                                            )
                                                .replace(
                                                    '{season}',
                                                    item.season,
                                                )
                                                .replace(
                                                    '{episode}',
                                                    item.episode,
                                                );

                                            if (
                                                item.objectSubtype ===
                                                    'MOVIE' ||
                                                item.objectSubtype !== 'VIDEO'
                                            ) {
                                                contentSubtitle = item.title;
                                            }

                                            return (
                                                <ContentBox
                                                    key={
                                                        new Date().getTime() +
                                                        index
                                                    }
                                                    id={item.id}
                                                    data={item}
                                                    horizontal
                                                    duration={formatContentDuration(
                                                        item.duration,
                                                    )}
                                                    highlight={item.highlight}
                                                    background={
                                                        item &&
                                                        contentUrlSolver(
                                                            config,
                                                            item.image,
                                                            'THUMBNAIL',
                                                            '?imwidth=300',
                                                        )
                                                    }
                                                    title={
                                                        item.objectSubtype ===
                                                        'MOVIE'
                                                            ? 'MOVIES'
                                                            : item.title
                                                    }
                                                    sub={contentSubtitle}
                                                    action={() =>
                                                        handleClickEp(item.id)
                                                    }
                                                />
                                            );
                                        })
                                    )}
                                </div>
                                {false && <GradientOverlay />}
                            </UpNextContainer>

                            <BackgroundOverlay />

                            <Separator />
                        </Grid>

                        {currentContent &&
                            currentContent.objectSubtype !== 'MOVIE' &&
                            (!mightLike || mightLike.length > 0) && (
                                <CarrouselContainer>
                                    {renderMightLike}
                                </CarrouselContainer>
                            )}
                    </>
                )}
            </Container>
        </>
    );
};

export default Player;
